import { Field } from '@catering/ui';
import type { JSX } from 'react';
import { useEffect } from 'react';
import {
  useFieldArray,
  type ArrayPath,
  type Control,
  type FieldArray,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
  type UseFormRegister,
} from 'react-hook-form';

import {
  defaultMenuLine,
  syncLinesToPositions,
  type MenuLineFormValues,
} from '../../../orders/menu-line-form-schema';

export interface MenuLineEditorPosition {
  positionId: string;
  positionName: string;
  serviceTypeId: string;
  serviceTypeName: string;
  displayOrder: number;
}

export interface MenuLineEditorProps<
  TFieldValues extends FieldValues & { items: MenuLineFormValues[] },
> {
  control: Control<TFieldValues>;
  register: UseFormRegister<TFieldValues>;
  /**
   * Active positions to render as rows, grouped by service type and
   * ordered by `displayOrder` within each group (`rotating-menu/design.md`
   * §4). `undefined` while positions are still loading — kept distinct
   * from an empty array so the sync effect below never wipes a pre-filled
   * row before the position list has resolved.
   */
  positions: MenuLineEditorPosition[] | undefined;
  errors: FieldErrors<TFieldValues>['items'];
}

type MenuLineFieldError = {
  quantity?: { message?: string };
  notes?: { message?: string };
};

/**
 * Shared by `OrderTemplateFormModal` and `ChangeRequestFormModal`
 * (`tasks.md` PR 6 REFACTOR, extended by `rotating-menu/design.md` §4):
 * one fixed row per active position, grouped by service type — no "add
 * line"/"remove line", since a template or change request line is never
 * an arbitrary menu pick, always the position's own row. `OrderForm`'s
 * `OrderItemEditor` renders the same shape plus the day's dish name, kept
 * as its own component rather than a variant of this one.
 */
export function MenuLineEditor<TFieldValues extends FieldValues & { items: MenuLineFormValues[] }>({
  control,
  register,
  positions,
  errors,
}: MenuLineEditorProps<TFieldValues>): JSX.Element {
  const { fields, replace } = useFieldArray({
    control,
    name: 'items' as ArrayPath<TFieldValues>,
  });
  const positionIdsKey =
    positions === undefined ? undefined : positions.map((position) => position.positionId).join(',');

  useEffect(() => {
    if (positionIdsKey === undefined) {
      return;
    }

    const positionIds = positionIdsKey === '' ? [] : positionIdsKey.split(',');
    const current = fields as unknown as MenuLineFormValues[];
    const next = syncLinesToPositions(current, positionIds, defaultMenuLine);

    if (
      next.length !== current.length ||
      next.some((item, index) => item.positionId !== current[index]?.positionId)
    ) {
      replace(next as unknown as FieldArray<TFieldValues, ArrayPath<TFieldValues>>[]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `fields`/`replace` intentionally excluded: this effect only re-syncs when the set of available positions changes, never on every keystroke.
  }, [positionIdsKey]);

  if (positions === undefined) {
    return <p className="text-sm text-slate-500">Buscando las posiciones activas…</p>;
  }

  const groups = groupByServiceType(positions);

  if (positions.length === 0) {
    return <p className="text-sm text-slate-500">No hay posiciones activas para cargar líneas.</p>;
  }

  return (
    <div className="flex flex-col gap-5">
      {groups.map((group) => (
        <div key={group.serviceTypeId} className="flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {group.serviceTypeName}
          </h3>
          {group.positions.map((position) => {
            const index = positions.findIndex((row) => row.positionId === position.positionId);
            const field = fields[index];

            if (field === undefined) {
              return null;
            }

            const lineError: MenuLineFieldError | undefined = Array.isArray(errors)
              ? (errors as MenuLineFieldError[])[index]
              : undefined;

            return (
              <div
                key={field.id}
                data-testid={`menu-line-row-${position.positionId}`}
                className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr_2fr] sm:items-end"
              >
                <div className="flex flex-col justify-end pb-2.5">
                  <span className="text-sm font-medium text-slate-900">
                    {position.positionName}
                  </span>
                </div>

                <Field
                  label="Cantidad"
                  type="number"
                  min={0}
                  defaultValue={(field as unknown as MenuLineFormValues).quantity}
                  {...register(`items.${index}.quantity` as FieldPath<TFieldValues>)}
                  {...(lineError?.quantity?.message === undefined
                    ? {}
                    : { error: lineError.quantity.message })}
                />

                <Field
                  label="Notas"
                  defaultValue={(field as unknown as MenuLineFormValues).notes}
                  {...register(`items.${index}.notes` as FieldPath<TFieldValues>)}
                  {...(lineError?.notes?.message === undefined
                    ? {}
                    : { error: lineError.notes.message })}
                />
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

interface ServiceTypeGroup {
  serviceTypeId: string;
  serviceTypeName: string;
  positions: MenuLineEditorPosition[];
}

function groupByServiceType(positions: MenuLineEditorPosition[]): ServiceTypeGroup[] {
  const groups = new Map<string, ServiceTypeGroup>();

  for (const position of [...positions].sort((a, b) => a.displayOrder - b.displayOrder)) {
    const existing = groups.get(position.serviceTypeId);

    if (existing === undefined) {
      groups.set(position.serviceTypeId, {
        serviceTypeId: position.serviceTypeId,
        serviceTypeName: position.serviceTypeName,
        positions: [position],
      });
    } else {
      existing.positions.push(position);
    }
  }

  return [...groups.values()];
}
